import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Download, ShieldCheck, X, Loader2 } from "lucide-react";
import { PRODUCTS, SITE } from "@/lib/site";
import PageHero, { ScheduleConsultationButton } from "@/components/site/PageHero";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { submitStoreLead } from "@/lib/api";
import { toast } from "sonner";

export default function Store() {
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);

  function open(p) {
    setSelected(p);
    setForm({ first_name: "", last_name: "", email: "", phone: "" });
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error("Please fill in name and email.");
      return;
    }
    setLoading(true);
    try {
      await submitStoreLead({
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone || undefined,
        product_slug: selected.slug,
        product_title: selected.title,
        price: selected.price,
        paypal_url: selected.paypalUrl,
      });
      toast.success("Redirecting you to secure PayPal checkout…");
      window.open(selected.paypalUrl, "_blank", "noopener,noreferrer");
      setSelected(null);
    } catch (err) {
      toast.error("Could not start checkout. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div data-testid="store-page">
      <PageHero
        eyebrow="TRJ Services Resource Center™"
        title="Professional tools, workbooks, and planning resources."
        subtitle="Practical digital products designed to help individuals, businesses, nonprofits, and organizations build stronger financial foundations — instant access after purchase."
        image={SITE.assets.officeModern}
      >
        <a href="#products" className="trj-btn-green" data-testid="store-hero-browse-btn">
          Download Resources <Download className="w-4 h-4" />
        </a>
        <ScheduleConsultationButton variant="gold" testid="store-hero-schedule-btn" calendlyOverride="https://calendly.com/trjservicesinc/consultation?month=2026-06" />
      </PageHero>

      <section id="products" className="bg-white py-20 md:py-24 scroll-mt-28" data-testid="store-products">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="trj-eyebrow mb-3">Digital Resource Bundles</div>
            <h2 className="font-serif-trj text-3xl md:text-4xl text-[var(--trj-navy)] leading-tight">Choose your bundle.</h2>
            <p className="mt-4 text-[var(--trj-muted)] leading-relaxed">
              Each bundle is delivered as a digital package. After successful payment via PayPal, you&apos;ll receive a confirmation email and our team will follow up with your download links.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-7">
            {PRODUCTS.map((p) => (
              <article
                key={p.slug}
                data-testid={`product-card-${p.slug}`}
                className="bg-white border border-gray-200 flex flex-col overflow-hidden transition-shadow hover:shadow-2xl"
              >
                <div className="aspect-[1/1] bg-[var(--trj-light)] overflow-hidden relative">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                  {p.badge && (
                    <div className="absolute top-4 left-4 bg-[var(--trj-gold)] text-[var(--trj-navy)] text-[10px] uppercase tracking-widest px-3 py-1 font-bold">
                      {p.badge}
                    </div>
                  )}
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <h3 className="font-serif-trj text-2xl text-[var(--trj-navy)] leading-snug">{p.title}</h3>
                  <p className="mt-3 text-sm text-[var(--trj-muted)] leading-relaxed">{p.description}</p>
                  <div className="mt-5 pt-5 border-t border-gray-200">
                    <div className="text-[10px] uppercase tracking-widest text-[var(--trj-green)] font-bold">Includes</div>
                    <ul className="mt-3 space-y-1.5">
                      {p.includes.map((it) => (
                        <li key={it} className="flex items-start gap-2 text-[13px] text-[var(--trj-navy)]">
                          <CheckCircle2 className="w-4 h-4 mt-0.5 text-[var(--trj-green)]" /> {it}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-auto pt-7 flex items-end justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[var(--trj-muted)]">Price</div>
                      <div className="font-serif-trj text-4xl text-[var(--trj-navy)]">${p.price}</div>
                    </div>
                    <button
                      onClick={() => open(p)}
                      className="trj-btn-green text-sm"
                      data-testid={`product-buy-${p.slug}`}
                    >
                      Buy Now <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-10 flex items-center justify-center gap-2 text-[12px] text-[var(--trj-muted)]">
            <ShieldCheck className="w-3.5 h-3.5 text-[var(--trj-green)]" />
            Secure checkout via PayPal · Pay with PayPal or any major credit/debit card
          </div>
        </div>
      </section>

      <section className="bg-[var(--trj-light)] py-16 text-center" data-testid="store-cta">
        <div className="max-w-3xl mx-auto px-6">
          <div className="trj-eyebrow mb-3">Need Personal Guidance?</div>
          <h2 className="font-serif-trj text-3xl md:text-4xl text-[var(--trj-navy)] leading-tight">
            Schedule a consultation with TRJ Services.
          </h2>
          <div className="mt-7 flex justify-center flex-wrap gap-3">
            <ScheduleConsultationButton testid="store-cta-schedule-btn" calendlyOverride="https://calendly.com/trjservicesinc/consultation?month=2026-06" />
            <Link to="/contact" className="trj-btn-outline-navy" data-testid="store-cta-contact-btn">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* LEAD CAPTURE + PayPal redirect */}
      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DialogContent data-testid="store-lead-dialog" className="sm:max-w-md rounded-none border-t-4 border-[var(--trj-gold)] p-0">
          <div className="bg-[var(--trj-navy)] text-white px-7 py-5 relative">
            <button onClick={() => setSelected(null)} className="absolute right-4 top-4 text-white/60 hover:text-white" data-testid="store-lead-close">
              <X className="w-4 h-4" />
            </button>
            <div className="trj-eyebrow !text-[var(--trj-gold)] mb-2">Checkout</div>
            <DialogHeader className="p-0">
              <DialogTitle className="font-serif-trj text-2xl text-white leading-tight">{selected?.title}</DialogTitle>
              <DialogDescription className="text-white/70 text-sm">${selected?.price} USD · Instant download after payment</DialogDescription>
            </DialogHeader>
          </div>
          <form onSubmit={onSubmit} className="px-7 py-6 bg-white space-y-4" data-testid="store-lead-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-[var(--trj-muted)]">First Name *</Label>
                <Input required value={form.first_name} onChange={(e) => setForm((s) => ({ ...s, first_name: e.target.value }))} className="rounded-none mt-1" data-testid="store-lead-first-name" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-[var(--trj-muted)]">Last Name *</Label>
                <Input required value={form.last_name} onChange={(e) => setForm((s) => ({ ...s, last_name: e.target.value }))} className="rounded-none mt-1" data-testid="store-lead-last-name" />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-[var(--trj-muted)]">Email *</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} className="rounded-none mt-1" data-testid="store-lead-email" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-[var(--trj-muted)]">Phone (optional)</Label>
              <Input value={form.phone} onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))} className="rounded-none mt-1" data-testid="store-lead-phone" />
            </div>
            <button type="submit" disabled={loading} className="trj-btn-gold w-full justify-center text-sm disabled:opacity-60" data-testid="store-lead-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {loading ? "Connecting…" : `Continue to PayPal · $${selected?.price}`}
            </button>
            <p className="text-[11px] text-[var(--trj-muted)] leading-relaxed">
              Your information is saved so we can send your download link and follow-up. Payment is processed securely by PayPal in a new tab.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
